import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { PageHeader } from "../components/AppLayout";
import { StatusBadge, OriginBadge, TypeTag } from "../components/Badges";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Check, X, PauseCircle, Wrench, Inbox } from "lucide-react";

const CATS = ["ALL", "CONTRADICTION", "OPEN_QUESTION_IMPACT", "CANDIDATE_CLAIM", "FLAGGED_FOR_REVIEW", "SOURCE_CONFLICT"];

const CAT_LABEL = {
  CONTRADICTION: { label: "Contradiction Radar", cls: "bg-red-50 text-red-700 border-red-200" },
  OPEN_QUESTION_IMPACT: { label: "Open-Question Impact", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  CANDIDATE_CLAIM: { label: "Candidate Claim", cls: "bg-slate-100 text-slate-700 border-slate-300" },
  FLAGGED_FOR_REVIEW: { label: "Flagged (Radar on Edit)", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  SOURCE_CONFLICT: { label: "Source Conflict", cls: "bg-orange-50 text-orange-700 border-orange-200" },
};

const PRIO = {
  HIGH: "bg-red-600 text-white", MEDIUM: "bg-amber-500 text-white",
  NORMAL: "bg-slate-200 text-slate-700", LOW: "bg-slate-100 text-slate-500",
};

// action sets per ref_type
const ACTIONS = {
  CLAIM: ["ACCEPT", "REJECT", "MODIFY", "DUPLICATE", "HISTORICAL", "SUPERSEDED", "CREATE_REVISION", "SUPERSEDE_EXISTING", "KEEP_OPEN"],
  KNOWLEDGE: ["ACCEPT", "REJECT", "MODIFY", "DUPLICATE", "HISTORICAL", "SUPERSEDED", "SUPERSEDE_EXISTING", "KEEP_OPEN"],
  SOURCE: ["ACCEPT", "REJECT", "KEEP_OPEN"],
};
const ACTION_LABEL = {
  ACCEPT: "Accept", REJECT: "Reject", MODIFY: "Modify", DUPLICATE: "Mark duplicate",
  HISTORICAL: "Mark historical", SUPERSEDED: "Mark superseded", CREATE_REVISION: "Create revision",
  SUPERSEDE_EXISTING: "Supersede existing item", KEEP_OPEN: "Keep open",
};

export default function ReviewQueue() {
  const [entries, setEntries] = useState([]);
  const [filter, setFilter] = useState("ALL");
  const [resolve, setResolve] = useState(null);

  const load = useCallback(() => api.get("/review-queue").then((r) => setEntries(r.data.entries)), []);
  useEffect(() => { load(); }, [load]);

  const quick = async (entry, action) => {
    try {
      await api.post("/review-queue/resolve", {
        entry_key: entry.entry_key, ref_type: entry.ref_type, ref_id: entry.ref_id, action,
      });
      toast.success(action === "KEEP_OPEN" ? "Kept open (acknowledged)." : `Resolved: ${ACTION_LABEL[action]}.`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    }
  };

  const shown = entries.filter((e) => filter === "ALL" || e.category === filter);

  return (
    <div>
      <PageHeader
        title="Review Queue"
        subtitle="Everything awaiting founder judgement — candidate claims, radar contradictions, edit flags, open-question impacts, proposed supersessions and source conflicts. Nothing auto-resolves."
      />
      <div className="p-10">
        <div className="flex flex-wrap gap-2 mb-6" data-testid="review-filters">
          {CATS.map((c) => (
            <button key={c} onClick={() => setFilter(c)} data-testid={`review-filter-${c}`}
              className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider border rounded-sm ${filter === c ? "bg-slate-900 text-white border-slate-900" : "border-slate-300 text-slate-500 hover:border-slate-500"}`}>
              {c === "ALL" ? `All (${entries.length})` : (CAT_LABEL[c]?.label || c)}
            </button>
          ))}
        </div>

        <div className="space-y-4" data-testid="review-queue-list">
          {shown.length === 0 && (
            <div className="border border-slate-200 bg-white p-10 text-center">
              <Inbox className="mx-auto text-slate-300 mb-3" size={28} />
              <p className="text-slate-400 text-sm">Queue clear. Nothing awaiting review.</p>
            </div>
          )}
          {shown.map((e, idx) => {
            const cat = CAT_LABEL[e.category] || { label: e.category, cls: "bg-slate-100 text-slate-700 border-slate-300" };
            const showHeader = idx === 0 || shown[idx - 1].bucket !== e.bucket;
            return (
              <div key={e.entry_key}>
                {showHeader && (
                  <div className="flex items-center gap-3 mt-8 first:mt-0 mb-3" data-testid={`bucket-header-${e.bucket}`}>
                    <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-slate-500">{e.bucket_label}</span>
                    <span className="h-px flex-1 bg-slate-200" />
                  </div>
                )}
                <div className="bg-white border border-slate-200 p-6" data-testid={`review-entry-${e.entry_key}`}>
                <div className="flex items-center gap-2 flex-wrap mb-3">
                  <span className={`font-mono text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm ${PRIO[e.priority] || PRIO.NORMAL}`}>{e.priority}</span>
                  <span className={`inline-flex items-center border px-2 py-0.5 rounded-sm font-mono text-[10px] uppercase tracking-wider ${cat.cls}`}>{cat.label}</span>
                  {e.knowledge_id && <span className="font-mono text-xs text-slate-700 bg-slate-100 px-1.5 rounded-sm">{e.knowledge_id}</span>}
                  {e.claim_origin && <OriginBadge origin={e.claim_origin} />}
                  {e.claim_type && <TypeTag type={e.claim_type} />}
                  <StatusBadge status={e.current_status} />
                  {e.resolution_status === "KEPT_OPEN" && <span className="font-mono text-[10px] uppercase text-slate-500 border border-slate-300 px-1.5 py-0.5 rounded-sm">kept open</span>}
                  <span className="font-mono text-[10px] text-slate-400 ml-auto">{e.flagged_at ? new Date(e.flagged_at).toLocaleString() : ""}</span>
                </div>

                <p className="text-slate-800 leading-relaxed">{e.reference}</p>
                <p className="text-sm text-slate-600 mt-1"><span className="font-mono text-[10px] uppercase text-slate-400">Reason:</span> {e.reason}</p>
                {e.resolution_note && <p className="text-xs text-slate-500 mt-1 italic">Note: {e.resolution_note}</p>}

                {e.related_conflict_ids?.length > 0 && (
                  <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    <span className="font-mono text-[10px] uppercase text-slate-400">Conflicts:</span>
                    {e.related_conflict_ids.map((id) => <span key={id} className="font-mono text-xs bg-red-50 text-red-700 border border-red-200 px-1.5 rounded-sm">{id}</span>)}
                  </div>
                )}

                <div className="text-[11px] font-mono text-slate-400 mt-2">
                  provenance: {e.provenance?.source_title || e.provenance?.knowledge_id || e.provenance?.source_family || "—"}
                  {e.provenance?.source_location ? ` · ${e.provenance.source_location}` : ""}
                </div>

                <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-slate-100">
                  <Button size="sm" onClick={() => quick(e, "ACCEPT")} className="rounded-sm bg-emerald-700 hover:bg-emerald-800 gap-1.5" data-testid={`review-accept-${e.entry_key}`}><Check size={14} /> Accept</Button>
                  <Button size="sm" variant="outline" onClick={() => quick(e, "REJECT")} className="rounded-sm gap-1.5 text-red-700" data-testid={`review-reject-${e.entry_key}`}><X size={14} /> Reject</Button>
                  <Button size="sm" variant="outline" onClick={() => quick(e, "KEEP_OPEN")} className="rounded-sm gap-1.5" data-testid={`review-keepopen-${e.entry_key}`}><PauseCircle size={14} /> Keep open</Button>
                  <Button size="sm" variant="outline" onClick={() => setResolve(e)} className="rounded-sm gap-1.5 border-slate-400" data-testid={`review-more-${e.entry_key}`}><Wrench size={14} /> Resolve…</Button>
                </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <ResolveDialog entry={resolve} onClose={() => setResolve(null)} onDone={() => { setResolve(null); load(); }} />
    </div>
  );
}

function ResolveDialog({ entry, onClose, onDone }) {
  const [action, setAction] = useState("");
  const [note, setNote] = useState("");
  const [modifiedText, setModifiedText] = useState("");
  const [target, setTarget] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (entry) {
      const acts = ACTIONS[entry.ref_type] || ["KEEP_OPEN"];
      setAction(acts[0]); setNote(""); setModifiedText(entry.reference || "");
      setTarget(entry.related_conflict_ids?.[0] || "");
    }
  }, [entry]);

  if (!entry) return null;
  const needsText = action === "MODIFY" || action === "CREATE_REVISION";
  const needsTarget = action === "SUPERSEDE_EXISTING" || (entry.ref_type === "CLAIM" && action === "CREATE_REVISION");
  const acts = ACTIONS[entry.ref_type] || [];

  const submit = async () => {
    if (needsText && !modifiedText.trim()) { toast.error("Provide the modified statement."); return; }
    if (needsTarget && !target.trim()) { toast.error("Provide the target knowledge ID."); return; }
    setBusy(true);
    try {
      await api.post("/review-queue/resolve", {
        entry_key: entry.entry_key, ref_type: entry.ref_type, ref_id: entry.ref_id, action,
        note, modified_text: needsText ? modifiedText : undefined,
        target_knowledge_id: needsTarget ? target : undefined,
      });
      toast.success(`Resolved: ${ACTION_LABEL[action]}. History preserved.`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Resolution failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!entry} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg rounded-sm bg-white" data-testid="resolve-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Resolve Review Item</DialogTitle>
          <DialogDescription className="text-sm text-slate-500">Choose an explicit disposition. Every resolution preserves history and provenance; doctrinal conflicts are never auto-resolved.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="font-mono text-[11px] uppercase tracking-wider text-slate-500">Action</Label>
            <Select value={action} onValueChange={setAction}>
              <SelectTrigger className="mt-1.5 rounded-sm border-slate-300" data-testid="resolve-action"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">{acts.map((a) => <SelectItem key={a} value={a}>{ACTION_LABEL[a]}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {needsText && (
            <div>
              <Label className="font-mono text-[11px] uppercase tracking-wider text-slate-500">Revised statement</Label>
              <Textarea value={modifiedText} onChange={(e) => setModifiedText(e.target.value)} className="mt-1.5 rounded-sm border-slate-300 min-h-[90px]" data-testid="resolve-text" />
            </div>
          )}
          {needsTarget && (
            <div>
              <Label className="font-mono text-[11px] uppercase tracking-wider text-slate-500">Target knowledge ID to supersede/revise</Label>
              {entry.related_conflict_ids?.length ? (
                <Select value={target} onValueChange={setTarget}>
                  <SelectTrigger className="mt-1.5 rounded-sm border-slate-300" data-testid="resolve-target"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent className="bg-white">{entry.related_conflict_ids.map((id) => <SelectItem key={id} value={id}>{id}</SelectItem>)}</SelectContent>
                </Select>
              ) : (
                <Input value={target} onChange={(e) => setTarget(e.target.value)} placeholder="e.g. PR-C011" className="mt-1.5 rounded-sm border-slate-300" data-testid="resolve-target-input" />
              )}
            </div>
          )}
          <div>
            <Label className="font-mono text-[11px] uppercase tracking-wider text-slate-500">Reason / note</Label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} className="mt-1.5 rounded-sm border-slate-300 min-h-[44px]" data-testid="resolve-note" />
          </div>
          <Button onClick={submit} disabled={busy} className="w-full rounded-sm bg-slate-900 hover:bg-slate-800" data-testid="resolve-confirm">{busy ? "Working…" : `Apply: ${ACTION_LABEL[action]}`}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
